import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation as useLibraryTranslation, withTranslation as withLibraryTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import hoistNonReactStatics from 'hoist-non-react-statics';

import ENV from 'config/env.conf';
import { COUNTRY_CODES } from 'constants/address/counties.const';
import LSK from 'constants/core/localStorageKey.const';
import { selectCurrentSalonCountryCode, selectCurrentSalonTimeZone } from 'selectors/currentSalon.selectors';
import { formatDate as formatDateIntl } from 'utils/converters/time/intl.util';
import { determineUserLocale } from 'utils/date/format.util';
import lsSet from 'utils/dom/storage/lsSet.util';
import logger from 'utils/logger.util';
import fromComponentToName from 'utils/react/fromComponentToName.util';

const COUNTRY_CODES_WITHOUT_GDPR = ['US'];
const COUNTRY_CODES_WITH_VAT = ['UK', 'GB'];

export const LOCALE = Object.freeze({
  ae: 'en-AE',
  us: 'en-US',
  uk: 'en-GB',
});

const L = logger('GILTProvider');

export const DEFAULT_LOCALE = ENV.DEFAULT_LOCALE || LOCALE.uk;

const Context = React.createContext();

// New languages need to be added here
const I18N_LOCALES = Object.freeze({
  GB: 'en-GB',
  US: 'en-US',
});

export const withGilt = (Component) => {
  // find out the name of the wrapped component or use a default one
  const displayName = `withGilt(${fromComponentToName(Component)})`;

  const WithGilt = (props) => {
    const gilt = useGilt();

    return <Component {...props} {...gilt} />;
  };

  // static fields must be copied from the wrapped to the wrapper
  hoistNonReactStatics(WithGilt, Component);

  // nice display name for the wrapper
  WithGilt.displayName = displayName;

  return WithGilt;
};

export const GiltProvider = ({ children }) => {
  const defaultLocale = determineUserLocale();

  const [locale, setLocale] = useState(defaultLocale);
  const timeZone = useSelector(selectCurrentSalonTimeZone);
  const countryCode = useSelector(selectCurrentSalonCountryCode);
  const { i18n } = useTranslation(defaultLocale);

  const formatDate = useCallback((date, options) => formatDateIntl(date, { locale, timeZone, ...options }), [locale, timeZone]);

  const changeLocale = useCallback(
    (newLocale) => {
      L.info('()', 'Changing locale in GILT provider from: ', locale, ' to: ', newLocale);

      if (!String(newLocale) || newLocale === i18n.language) {
        return;
      }

      lsSet(LSK.locale, newLocale);
      i18n.changeLanguage(newLocale);
      setLocale(newLocale);
    },
    [i18n, locale],
  );

  useEffect(() => {
    const i18nLocale = I18N_LOCALES[countryCode] ?? I18N_LOCALES.GB;

    if (!countryCode || i18n.language === i18nLocale) {
      return;
    }

    changeLocale(i18nLocale);
  }, [changeLocale, countryCode, i18n.language]);

  const isSalonInUS = countryCode === COUNTRY_CODES.US;
  const isSalonInGB = countryCode === COUNTRY_CODES.GB;
  const isSalonWithGDPR = !COUNTRY_CODES_WITHOUT_GDPR.includes(countryCode);
  const isSalonWithVAT = COUNTRY_CODES_WITH_VAT.includes(countryCode);

  const value = useMemo(
    () => ({ locale, isSalonInUS, isSalonInGB, isSalonWithGDPR, isSalonWithVAT, changeLocale, formatDate }),
    [locale, isSalonInUS, isSalonInGB, isSalonWithGDPR, isSalonWithVAT, changeLocale, formatDate],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useGilt = () => {
  const value = useContext(Context);

  return useMemo(() => value, [value]);
};

/**
 * Hook wrapper for translations.
 * Returns the same shape as react-i18next's useTranslation but allows
 * adding custom logic (logging, telemetry, fallback handling) in one place.
 */
export const useTranslation = (ns) => useLibraryTranslation(ns);
// Example: we could add logging here in the future
// const { t, i18n, ready } = useTranslation(ns);
// if (!ready) { console.debug('translation not ready', ns); }

/**
 * HOC wrapper for translations.
 * Usage: export default withTrans()(MyComponent)
 */
export const withTranslation = (...args) => withLibraryTranslation(...args);
